/**
 * PR-20 — Survey Intelligence storage + reports.
 *
 * Capa de demo (localStorage) para registrar respuestas de encuestas y
 * generar reportes agregados que consume el Survey Intelligence Center,
 * el SuperAdmin Strategic AI (vía contextHint) y el Partner Portal.
 */
import { trackEvent } from "@/lib/audit";
import { SURVEY_BANK, SURVEY_CATEGORIES, type SurveyCategory, type SurveyQuestion } from "@/lib/polls/surveyBank";

export interface SurveyResponse {
  id: string;
  poll_id: string;
  question_type: SurveyCategory;
  question: string;
  answer: string;
  user_id?: string | null;
  guest_id?: string | null;
  country?: string | null;
  city?: string | null;
  location_context?: string | null;
  favorite_team?: string | null;
  match_id?: string | null;
  sponsor_id?: string | null;
  campaign_id?: string | null;
  timestamp: number;
  device?: "mobile" | "desktop" | "tablet";
  language?: string;
  reward_granted?: boolean;
  conversion_intent_score?: number;
}

const KEY = "vs:pr20:survey_responses";
const MAX = 5000;

function read(): SurveyResponse[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SurveyResponse[]) : [];
  } catch {
    return [];
  }
}

function write(rows: SurveyResponse[]): void {
  if (typeof window === "undefined") return;
  const tail = rows.slice(-MAX);
  window.localStorage.setItem(KEY, JSON.stringify(tail));
}

const PURCHASE_INTENT_ANSWERS = new Set(["Sí", "Si", "Tal vez", "Comida", "Bebida", "Pizza", "Descuento", "Cupón", "Bono digital", "VIP"]);

function inferIntentScore(category: SurveyCategory, answer: string): number {
  if (category === "purchase_intent" || category === "sponsor_activation" || category === "halftime_commercial") {
    if (answer.toLowerCase() === "sí" || answer.toLowerCase() === "si") return 0.9;
    if (PURCHASE_INTENT_ANSWERS.has(answer)) return 0.7;
    if (answer.toLowerCase() === "no") return 0.1;
    return 0.5;
  }
  if (category === "food_beverage") return PURCHASE_INTENT_ANSWERS.has(answer) ? 0.6 : 0.3;
  return 0.2;
}

export interface RecordResponseInput {
  question: SurveyQuestion;
  answer: string;
  sponsor_id?: string;
  campaign_id?: string;
  match_id?: string;
  country?: string;
  city?: string;
  location_context?: string;
  reward_granted?: boolean;
  device?: "mobile" | "desktop" | "tablet";
  language?: string;
}

export function recordSurveyResponse(input: RecordResponseInput): SurveyResponse {
  const score = inferIntentScore(input.question.category, input.answer);
  const row: SurveyResponse = {
    id: `sr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    poll_id: input.question.id,
    question_type: input.question.category,
    question: input.question.question,
    answer: input.answer,
    timestamp: Date.now(),
    sponsor_id: input.sponsor_id ?? null,
    campaign_id: input.campaign_id ?? null,
    match_id: input.match_id ?? null,
    country: input.country ?? null,
    city: input.city ?? null,
    location_context: input.location_context ?? null,
    reward_granted: !!input.reward_granted,
    device: input.device,
    language: input.language,
    conversion_intent_score: score,
  };
  const rows = read();
  rows.push(row);
  write(rows);
  void trackEvent("survey_answer_recorded", {
    poll_id: row.poll_id, category: row.question_type, intent: score, sponsor_id: row.sponsor_id,
  });
  if (input.question.category === "food_beverage") {
    void trackEvent("consumption_signal_detected", { answer: row.answer, city: row.city });
  }
  if (score >= 0.7) {
    void trackEvent("purchase_intent_detected", { score, category: row.question_type, answer: row.answer });
  }
  if (input.sponsor_id) {
    void trackEvent("sponsor_interest_detected", { sponsor_id: input.sponsor_id, intent: score });
  }
  return row;
}

/** Seed a few demo responses so reports render with data on first boot. */
export function seedDemoResponsesIfEmpty(): void {
  if (read().length > 0) return;
  const cities = ["Miami", "Los Ángeles", "CDMX", "NY", "Houston"];
  for (let i = 0; i < 60; i += 1) {
    const q = SURVEY_BANK[i % SURVEY_BANK.length];
    const ans = q.options[i % q.options.length];
    recordSurveyResponse({
      question: q,
      answer: ans,
      city: cities[i % cities.length],
      country: "US",
      sponsor_id: i % 3 === 0 ? "coca_cola" : i % 3 === 1 ? "toyota" : "atnt",
      campaign_id: `wc26_${i % 4}`,
      match_id: "mx_vs_usa_demo",
      device: i % 2 === 0 ? "mobile" : "desktop",
      language: "es",
      reward_granted: q.rewardEligible && i % 4 === 0,
    });
  }
}

export function getAllResponses(): SurveyResponse[] {
  return read();
}

export function clearResponses(): void {
  write([]);
}

// ---------- Reports ----------

export interface Tally { label: string; count: number; pct: number }

function tally(rows: SurveyResponse[], keyFn: (r: SurveyResponse) => string | null | undefined): Tally[] {
  const map = new Map<string, number>();
  let total = 0;
  for (const r of rows) {
    const k = keyFn(r);
    if (!k) continue;
    map.set(k, (map.get(k) ?? 0) + 1);
    total += 1;
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count, pct: total ? Math.round((count / total) * 100) : 0 }))
    .sort((a, b) => b.count - a.count);
}

export interface SurveyReport {
  title: string;
  generatedAt: number;
  totalResponses: number;
  sections: Array<{ heading: string; tallies: Tally[] }>;
}

function filterByCategories(cats: SurveyCategory[]): SurveyResponse[] {
  const s = new Set(cats);
  return read().filter((r) => s.has(r.question_type));
}

export function consumptionReport(): SurveyReport {
  const rows = filterByCategories(["food_beverage"]);
  return {
    title: "Consumption Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [
      { heading: "Top food/drink answers", tallies: tally(rows, (r) => r.answer).slice(0, 10) },
      { heading: "By city", tallies: tally(rows, (r) => r.city) },
    ],
  };
}

export function sponsorPerformanceReport(): SurveyReport {
  const rows = read().filter((r) => r.sponsor_id);
  return {
    title: "Sponsor Performance Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [
      { heading: "Sponsor reach", tallies: tally(rows, (r) => r.sponsor_id) },
      { heading: "Campaigns", tallies: tally(rows, (r) => r.campaign_id) },
    ],
  };
}

export function brandPreferenceReport(): SurveyReport {
  const rows = filterByCategories(["brand_preference", "halftime_commercial"]);
  return {
    title: "Brand Preference Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [{ heading: "Brands mentioned", tallies: tally(rows, (r) => r.answer).slice(0, 10) }],
  };
}

export function purchaseIntentReport(): SurveyReport {
  const rows = filterByCategories(["purchase_intent", "sponsor_activation"]);
  const high = rows.filter((r) => (r.conversion_intent_score ?? 0) >= 0.7);
  return {
    title: "Purchase Intent Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [
      { heading: "High-intent answers", tallies: tally(high, (r) => r.answer).slice(0, 10) },
      { heading: "By city", tallies: tally(rows, (r) => r.city) },
    ],
  };
}

export function cityDemandReport(): SurveyReport {
  const rows = read();
  return {
    title: "City Demand Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [
      { heading: "Top cities by responses", tallies: tally(rows, (r) => r.city) },
      { heading: "Top cities by purchase intent", tallies: tally(rows.filter((r) => (r.conversion_intent_score ?? 0) >= 0.7), (r) => r.city) },
    ],
  };
}

export function halftimeCommercialReport(): SurveyReport {
  const rows = filterByCategories(["halftime_commercial"]);
  return {
    title: "Halftime Commercial Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [{ heading: "Ad recall & promo demand", tallies: tally(rows, (r) => r.answer).slice(0, 10) }],
  };
}

export function postMatchFanInsightReport(): SurveyReport {
  const rows = filterByCategories(["post_match_insight", "fan_profile"]);
  return {
    title: "Post-Match Fan Insight Report",
    generatedAt: Date.now(),
    totalResponses: rows.length,
    sections: [
      { heading: "Top fan signals", tallies: tally(rows, (r) => r.answer).slice(0, 10) },
      { heading: "Fan type breakdown", tallies: tally(rows.filter((r) => r.question_type === "fan_profile"), (r) => r.answer) },
    ],
  };
}

export const ALL_REPORT_BUILDERS: Array<{ id: string; build: () => SurveyReport }> = [
  { id: "consumption", build: consumptionReport },
  { id: "sponsor_performance", build: sponsorPerformanceReport },
  { id: "brand_preference", build: brandPreferenceReport },
  { id: "purchase_intent", build: purchaseIntentReport },
  { id: "city_demand", build: cityDemandReport },
  { id: "halftime_commercial", build: halftimeCommercialReport },
  { id: "post_match_insight", build: postMatchFanInsightReport },
];

export function reportToMarkdown(r: SurveyReport): string {
  const lines: string[] = [];
  lines.push(`# ${r.title}`);
  lines.push(`Generated: ${new Date(r.generatedAt).toISOString()}`);
  lines.push(`Total responses: **${r.totalResponses}**`);
  for (const s of r.sections) {
    lines.push("");
    lines.push(`## ${s.heading}`);
    if (s.tallies.length === 0) { lines.push("_Sin datos._"); continue; }
    for (const t of s.tallies) lines.push(`- ${t.label} — **${t.count}** (${t.pct}%)`);
  }
  return lines.join("\n");
}

export function reportToCsv(r: SurveyReport): string {
  const rows = [["section", "label", "count", "pct"]];
  for (const s of r.sections) for (const t of s.tallies) rows.push([s.heading, t.label, String(t.count), String(t.pct)]);
  return rows.map((r2) => r2.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
}

/** Quick text snapshot fed to the Strategic AI as contextHint. */
export function buildStrategicAiContext(): string {
  const lines: string[] = ["SURVEY INTELLIGENCE SNAPSHOT (PR-20)"];
  for (const b of ALL_REPORT_BUILDERS) {
    const r = b.build();
    lines.push(`\n## ${r.title} (n=${r.totalResponses})`);
    for (const s of r.sections.slice(0, 2)) {
      lines.push(`- ${s.heading}: ${s.tallies.slice(0, 5).map((t) => `${t.label} ${t.pct}%`).join(", ") || "—"}`);
    }
  }
  lines.push(`\n## Catalog summary`);
  for (const c of SURVEY_CATEGORIES) {
    lines.push(`- ${c.label}: ${SURVEY_BANK.filter((q) => q.category === c.id).length} preguntas`);
  }
  return lines.join("\n");
}